import React, { useState, useEffect } from "react";
import "./Organization.css";
import { Button, Text, DataTable, TextInput, Box } from "grommet";
import CommonButton from "../../../common/Button/CommonButton";
import CommonSearchInput from "../../../common/SearchInput/CommonSearchInput";
import searchIcon from "../../../../../../assets/search.svg";
import Pagination from "../../../common/Pagination/Pagination";
import { APIs } from "../../../../../../util/api/APIs";
import SafeairManager from "../../../../../../util/api/SafeairManager";
import { useLocation } from "react-router-dom";
import { useStore } from "../../../../../../Hooks/useStore";

function ViewSubscription() {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [searchString, setSearchString] = useState("");
  const location = useLocation();
  const orgId = location.search?.substring(1);
  const [subscriptions, setSubscriptions] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const { rootStore } = useStore();

  useEffect(() => {
    const fetchSubscriptions = async () => {
      if (!orgId) return;

      try {
        rootStore.loadingStore.setShowLoader(true);
        const result = await SafeairManager.getallSubscriptionByOrgId({
          api: APIs.Form.getallSubscriptionByOrgId,
          parameters: { orgId },
        });

        setSubscriptions(result);
        setFilteredData(result);
      } catch (error) {
        console.error("Failed to fetch subscriptions:", error);
      } finally {
        rootStore.loadingStore.setShowLoader(false);
      }
    };

    fetchSubscriptions();
  }, [orgId]);

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are 0-based
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const formatBoolean = (value) => {
    if (value === true) return "Yes";
    if (value === false) return "No";
    return "-";
  };

  const columns = [
    {
      align: "center",
      property: "startDate",
      header: <Text>Start Date</Text>,
      render: (datum) => <Text>{datum.startDate ?? "-"}</Text>,
    },
    {
      align: "center",
      property: "endDate",
      header: <Text>End Date</Text>,
      render: (datum) => <Text>{datum.endDate ?? "-"}</Text>,
    },
    {
      align: "center",
      property: "status",
      header: <Text>Status</Text>,
      render: (datum) => <Text>{datum.status || "-"}</Text>,
    },
    {
      align: "center",
      property: "active",
      header: <Text>Active</Text>,
      render: (datum) => <Text>{String(datum.active)}</Text>,
    },
    {
      align: "center",
      property: "timestamp",
      header: <Text>Created At</Text>,
      render: (datum) => <Text>{formatDate(datum.timestamp)}</Text>,
    },
    {
      align: "center",
      property: "createdBy",
      header: <Text>Created By</Text>,
      render: (datum) => <Text>{datum.createdBy || "-"}</Text>,
    },
  ];

  const onSearchStrChange = (value) => {
    setSearchString(value);
    if (!value.trim()) {
      setFilteredData(subscriptions);
      return;
    }

    const lowerCaseValue = value.toLowerCase();
    const filtered = subscriptions.filter((item) => {
      return (
        (item.startDate && item.startDate.toLowerCase().includes(lowerCaseValue)) ||
        (item.endDate && item.endDate.toLowerCase().includes(lowerCaseValue)) ||
        (item.status && item.status.toLowerCase().includes(lowerCaseValue)) ||
        (item.timestamp && item.timestamp.toLowerCase().includes(lowerCaseValue)) ||
        (item.createdBy && item.createdBy.toLowerCase().includes(lowerCaseValue)) ||
        (item.active !== undefined && 
          (item.active ? "yes" : "no").includes(lowerCaseValue))
      );
    });
    setFilteredData(filtered);
    setCurrentPage(1); // Reset to first page when searching
  };

  const onSearch = () => {
    // Triggered by search button, but we're already filtering on change
    // Could add additional search logic here if needed
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      onSearch();
    }
  };

  const totalItems = filteredData.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const visibleData = filteredData.slice(startIndex, endIndex);

  return (
    <Box className="organization-screen-container">
      <div className="organization-screen-header">
        <div className="organization-screen-left-header">
          <div style={{ position: "relative" }}>
            <CommonSearchInput
              style={{
                padding: "8px 8px 8px 35px",
                borderRadius: "10px",
                background: "#F2F8FF",
                outline: "0",
                border: "none",
                color: "#656566",
                fontFamily: "Inter",
                fontSize: "14px",
                fontStyle: "normal",
                fontWeight: "400",
                lineHeight: "18px",
                height: "40px",
                maxWidth: "320px",
                width: "320px",
              }}
              placeholder="Search"
              value={searchString}
              onchange={(e) => onSearchStrChange(e.target.value)}
              onkeydown={(e) => handleKeyDown(e)}
            />
            <img
              style={{
                position: "absolute",
                left: "12px",
                top: "50%",
                transform: "translateY(-50%)",
                width: "16px",
                height: "16px",
              }}
              src={searchIcon}
              className="searchIcon"
              alt="Search"
            />
          </div>
          <CommonButton
            title="Search"
            onclick={() => onSearch()}
            padding="12px 24px 12px 28px"
            color="#fff"
            bgColor="#357EFF"
            radius={10}
            border="1px solid #357EFF"
            lineheight="14px"
            ml={5}
          />
        </div>
      </div>

      <div style={{ overflow: "auto", marginTop: 16 }}>
        <DataTable
          columns={columns}
          data={visibleData}
          resizeable
          step={itemsPerPage}
          className="organization-table"
        />
      </div>

      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalItems}
          onPageChange={handlePageChange}
        />
      )}
    </Box>
  );
}

export default ViewSubscription;